import { ReactElement, ReactNode, type Ref } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  type RefreshControlProps,
  ScrollView,
  type ScrollViewProps,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { Edge, SafeAreaView } from 'react-native-safe-area-context';

import { colors, contentWidths, spacing } from '@/constants/theme';

type ColorToken = keyof typeof colors;
type SpacingToken = keyof typeof spacing;
type PaddingToken = SpacingToken | 'none';
type WidthToken = keyof typeof contentWidths;

export type ScreenProps = {
  children: ReactNode;
  /** Wrap children in a ScrollView. Default true. Pass false for fixed layouts. */
  scroll?: boolean;
  /**
   * Safe-area edges. Default `['top']` — correct for tab screens, since
   * Expo Router's `<Tabs>` already handles bottom inset. Pass
   * `['top', 'bottom']` for full-screen / modal / auth screens.
   */
  edges?: readonly Edge[];
  /** Background color token. Default `'background'`. */
  background?: ColorToken;
  /**
   * Outer padding token applied to ScrollView contentContainer (when scrolling)
   * or to a wrapping View (when not). Default `'lg'` (24px). Pass `'none'`
   * for screens that handle their own padding (e.g. full-bleed images).
   */
  padding?: PaddingToken;
  /**
   * Cap the content column and center it. Default `undefined` = full bleed,
   * which is correct on a phone and wrong on a desktop browser for anything
   * with a grid or a sized image. `'column'` (480) for a single column of
   * fields, copy, or one image; `'wide'` (760) for multi-column layouts.
   *
   * NOT platform-gated — it caps any viewport wider than the token, native
   * included. Today that's web-only in practice because the iOS build is
   * portrait-only and `supportsTablet: false` (app.json), so native never
   * exceeds ~440pt. Deliberate: an iPad would want the same cap for the same
   * reason. If tablet or landscape support is ever turned on, re-verify these
   * screens there rather than assuming this stayed a web-only concern.
   *
   * Applied INSIDE the scroll padding so the cap is the content's own width,
   * not the width including gutters — otherwise the same token yields a
   * different column on screens with different padding.
   *
   * Sharp edge: `contentContainerStyle` still lands on the scroll container,
   * which now has ONE child (this wrapper) instead of the children directly.
   * Layout properties that act on children — `gap`, `alignItems` — therefore
   * stop doing what they did. No caller combines the two today; if you need
   * both, put the child-affecting styles on a View inside `children`.
   */
  width?: WidthToken;
  /** Wrap in KeyboardAvoidingView. Opt-in for forms. */
  keyboardAvoiding?: boolean;
  contentContainerStyle?: StyleProp<ViewStyle>;
  /**
   * Pull-to-refresh control, passed straight to the inner ScrollView. Only
   * applies when `scroll` is true (the default). Pass a `<RefreshControl>`.
   */
  refreshControl?: ReactElement<RefreshControlProps>;
  /** Scroll listener for the inner ScrollView (scroll screens only). */
  onScroll?: ScrollViewProps['onScroll'];
  /** The inner ScrollView, for screens that scroll to something (scroll screens only). */
  scrollRef?: Ref<ScrollView>;
  testID?: string;
};

const DEFAULT_EDGES: readonly Edge[] = ['top'];

export function Screen({
  children,
  scroll = true,
  edges = DEFAULT_EDGES,
  background = 'background',
  padding = 'lg',
  width,
  keyboardAvoiding = false,
  contentContainerStyle,
  refreshControl,
  onScroll,
  scrollRef,
  testID,
}: ScreenProps) {
  const paddingValue = padding === 'none' ? 0 : spacing[padding];
  const bgStyle = { backgroundColor: colors[background] };

  // No wrapper at all when uncapped: every existing screen keeps the exact
  // tree it has today. When capped, `scroll=false` layouts need the wrapper
  // to carry flex so `flex: 1` children still fill the height — but a scroll
  // container must NOT (flexBasis 0 inside a content-sized contentContainer
  // collapses the column to zero height).
  const column = width ? (
    <View
      testID="screen-column"
      style={[
        styles.column,
        !scroll && styles.flex,
        { maxWidth: contentWidths[width] },
      ]}
    >
      {children}
    </View>
  ) : (
    children
  );

  const inner = scroll ? (
    <ScrollView
      ref={scrollRef}
      style={styles.flex}
      contentContainerStyle={[{ padding: paddingValue }, contentContainerStyle]}
      keyboardShouldPersistTaps="handled"
      refreshControl={refreshControl}
      onScroll={onScroll}
      scrollEventThrottle={onScroll ? 16 : undefined}
    >
      {column}
    </ScrollView>
  ) : (
    <View style={[styles.flex, { padding: paddingValue }, contentContainerStyle]}>
      {column}
    </View>
  );

  const body = keyboardAvoiding ? (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      {inner}
    </KeyboardAvoidingView>
  ) : (
    inner
  );

  return (
    <SafeAreaView style={[styles.flex, bgStyle]} edges={edges} testID={testID}>
      {body}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  column: { width: '100%', alignSelf: 'center' },
});
